import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Building2,
  MapPin,
  DollarSign,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const redIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

function LocationPicker({ position, setPosition, setForm }) {
  const map = useMapEvents({
    click(e) {
      const { latlng } = e;
      setPosition(latlng);
      fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latlng.lat}&lon=${latlng.lng}&format=json`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (data?.display_name) {
            const city =
              data.address?.city ||
              data.address?.town ||
              data.address?.village ||
              "";
            setForm((prev) => ({
              ...prev,
              city,
              address: data.display_name,
            }));
          }
        })
        .catch(() => console.warn("Reverse geocoding failed"));
    },
  });

  useEffect(() => {
    if (position) map.flyTo(position, 14);
  }, [position]);

  return position ? (
    <Marker
      position={position}
      icon={redIcon}
      draggable
      eventHandlers={{
        dragend: (e) => {
          const newPos = e.target.getLatLng();
          setPosition(newPos);
          fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${newPos.lat}&lon=${newPos.lng}&format=json`,
          )
            .then((res) => res.json())
            .then((data) => {
              if (data?.display_name) {
                const city =
                  data.address?.city ||
                  data.address?.town ||
                  data.address?.village ||
                  "";
                setForm((prev) => ({
                  ...prev,
                  city,
                  address: data.display_name,
                }));
                toast.success("📍 Address updated on map", { duration: 2000 });
              }
            })
            .catch(() => console.warn("Reverse geocoding failed"));
        },
      }}
    />
  ) : null;
}

function CustomDropdown({
  label,
  icon,
  options,
  value,
  onChange,
  placeholder,
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="flex flex-col gap-2 w-1/2 relative" ref={dropdownRef}>
      <label className="text-[#302B2B] text-[14px] font-normal">{label}</label>
      <div
        onClick={() => setOpen(!open)}
        className="border border-[#9A99AB] rounded-xl py-3 text-[16px] bg-white cursor-pointer flex items-center justify-between hover:border-[#5563F6] transition px-3 shadow-sm"
      >
        <div className="flex items-center gap-2">
          {selectedOption?.icon ? (
            <img
              src={selectedOption.icon}
              alt={selectedOption.label}
              className="w-5 h-5 rounded-full"
            />
          ) : (
            <span className="text-[#9A99AB]">{icon}</span>
          )}
          <span
            className={`${value ? "text-[#302B2B]" : "text-[#9A99AB]"} truncate`}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-[#9A99AB] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 w-full bg-white border border-[#9A99AB] rounded-xl shadow-2xl z-[9999] overflow-hidden max-h-[220px] overflow-y-auto scrollbar-thin scrollbar-thumb-transparent scrollbar-track-transparent"
          >
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`px-4 py-2 cursor-pointer flex items-center gap-2 hover:bg-[#F5F6FF] ${
                  value === opt.value
                    ? "bg-[#F7F8FF] font-medium text-[#5563F6]"
                    : "text-[#302B2B]"
                }`}
              >
                {opt.icon && (
                  <img
                    src={opt.icon}
                    alt={opt.label}
                    className="w-5 h-5 rounded-full"
                  />
                )}
                <span>{opt.label}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function InputField({ label, value, onChange, disabled = false }) {
  return (
    <div className="flex flex-col gap-2 w-1/2" style={{ width: "100%" }}>
      <label className="text-[#302B2B] text-[14px] font-normal">{label}</label>
      <input
        value={value}
        onChange={onChange}
        type="text"
        placeholder={label}
        disabled={disabled}
        className={`border border-[#9A99AB] rounded-xl px-3 py-3 text-[16px] w-full focus:outline-none ${
          disabled
            ? "bg-gray-100 text-gray-500 cursor-not-allowed"
            : "text-[#302B2B] focus:ring-2 focus:ring-[#5563F6]"
        }`}
      />
    </div>
  );
}

export default function EditBusinessModal({ onClose, userData }) {
  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const userType = userData?.user?.status;

  const [form, setForm] = useState({
    first_name: userData?.user?.first_name || "",
    last_name: userData?.user?.last_name || "",
    email: userData?.user?.email || "",
    company_name: userData?.user?.company_name || "",
    phone_number: userData?.phone_number || "",
    address: userData?.address || "",
    city: userData?.city || "",
    currency: userData?.currency || "USD",
  });

  const [currencies, setCurrencies] = useState([]);
  const [position, setPosition] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [addressSelected, setAddressSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const [addressFocused, setAddressFocused] = useState(false);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const headers = buildHeaders(token);
        const res = await fetch(`${API_URL}/service/currencies/`, { headers });
        const data = await res.json();
        if (Array.isArray(data)) setCurrencies(data);
      } catch {
        console.warn("Currency fetch failed");
      }
    };
    fetchCurrencies();
  }, [token]);

  useEffect(() => {
    if (userData?.lat && userData?.lng) {
      setPosition({ lat: userData.lat, lng: userData.lng });
      return;
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setPosition({ lat: 40.1056, lng: 44.522 }),
      );
    }
  }, [userData]);

  useEffect(() => {
    if (!position || userData?.address) return;

    const fetchAddress = async () => {
      try {
        const GOOGLE_KEY = "AIzaSyDiaYCEohP3g25tcOp4-J4shMMdH3bQ_as";
        const { lat, lng } = position;
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_KEY}`,
        );
        const data = await res.json();
        if (data.status === "OK" && data.results.length > 0) {
          const address = data.results[0].formatted_address;
          const cityComponent = data.results[0].address_components.find((c) =>
            c.types.includes("locality"),
          );
          const city = cityComponent ? cityComponent.long_name : "";
          setForm((prev) => ({ ...prev, city, address }));
        }
      } catch (err) {
        console.warn("Reverse geocoding failed", err);
      }
    };

    fetchAddress();
  }, [position, userData]);

  useEffect(() => {
    if (
      !addressFocused ||
      !form.address ||
      form.address.trim().length < 3 ||
      addressSelected
    ) {
      setSuggestions([]);
      return;
    }

    clearTimeout(debounceRef.current);

    const controller = new AbortController();

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/nominatim/search?q=${encodeURIComponent(
            form.address.trim(),
          )}&limit=5`,
          {
            signal: controller.signal,
          },
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        setSuggestions(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.warn("Autocomplete failed:", err);
          setSuggestions([]);
        }
      }
    }, 400);

    return () => {
      controller.abort();
      clearTimeout(debounceRef.current);
    };
  }, [form.address, addressSelected, addressFocused]);

  const handleChange = (field, value) => {
    if (field === "address") setAddressSelected(false);
    setForm({ ...form, [field]: value });
  };

  const handleAddressSelect = (item) => {
    setForm((prev) => ({ ...prev, address: item.display_name }));
    setPosition({ lat: parseFloat(item.lat), lng: parseFloat(item.lon) });
    setSuggestions([]);
    setAddressSelected(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const lat = position?.lat || 0;
      const lng = position?.lng || 0;
      const full_name =
        `${form.first_name.trim()} ${form.last_name.trim()}`.trim();

      const body = {
        full_name,
        email: form.email.trim(),
        company_name: userType === "individual" ? "" : form.company_name.trim(),
        phone_number: form.phone_number.trim(),
        city: form.city,
        address: form.address,
        lat,
        lng,
        address_url: `https://www.google.com/maps?q=${lat},${lng}`,
        currency: form.currency || "USD",
        time_zone: getUserTimeZone(),
      };

      const headers = buildHeaders(token);

      const res = await fetch(`${API_URL}/auth/userinfo/`, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);

      toast.success("Business info updated!");
      onClose(true);
    } catch (err) {
      console.error("❌ Error updating business info:", err);
      toast.error("❌ Failed to update business info.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 backdrop-blur-sm"
      style={{ marginTop: "0px" }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="bg-white rounded-[24px] w-[760px] shadow-2xl flex flex-col gap-6 max-h-[90vh] overflow-y-auto no-scrollbar p-8"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[20px] font-bold text-[#302B2B] flex items-center gap-2">
            <Building2 className="w-6 h-6 text-gray-700" />
            Edit Additional Information
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        <div className="flex flex-col gap-6">
          {/* First & Last Name */}
          <div className="flex gap-6">
            <InputField
              label="First Name"
              value={form.first_name}
              onChange={(e) => handleChange("first_name", e.target.value)}
            />
            <InputField
              label="Last Name"
              value={form.last_name}
              onChange={(e) => handleChange("last_name", e.target.value)}
            />
          </div>

          <div className="flex gap-6">
            <InputField
              label="Email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              disabled={true}
            />
            <InputField
              label="Phone Number"
              value={form.phone_number}
              onChange={(e) => handleChange("phone_number", e.target.value)}
            />
          </div>

          {userType === "individual" && (
            <>
              <div className="flex gap-6">
                <CustomDropdown
                  label="Currency"
                  icon={<DollarSign className="w-5 h-5" />}
                  options={currencies.map((c) => ({
                    value: c.short_name,
                    label: c.short_name,
                    icon: c.icon,
                  }))}
                  value={form.currency}
                  onChange={(val) => handleChange("currency", val)}
                  placeholder="Select currency"
                />
                <div className="flex-1">
                  <InputField
                    label="Address"
                    value={form.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 relative">
                <label className="text-[#302B2B] text-[14px] font-normal">
                  Address
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3.5">
                    <MapPin className="w-5 h-5 text-gray-400" />
                  </span>
                  <input
                    type="text"
                    value={form.address}
                    placeholder="Enter address"
                    onFocus={() => setAddressFocused(true)}
                    onBlur={() =>
                      setTimeout(() => setAddressFocused(false), 200)
                    }
                    onChange={(e) => handleChange("address", e.target.value)}
                    className="pl-10 border border-[#9A99AB] rounded-xl px-3 py-3 text-[16px] text-[#302B2B] placeholder-[#9A99AB] w-full focus:outline-none focus:ring-2 focus:ring-[#5563F6]"
                  />
                </div>

                {suggestions.length > 0 && (
                  <motion.div className="absolute top-full mt-2 left-0 w-full bg-white border border-[#9A99AB] rounded-xl shadow-lg z-[9999] max-h-[220px] overflow-y-auto no-scrollbar">
                    {suggestions.map((item) => (
                      <div
                        key={item.place_id}
                        onClick={() => handleAddressSelect(item)}
                        className="px-4 py-2 cursor-pointer hover:bg-[#F5F6FF] transition"
                      >
                        <div className="text-[14px] font-medium text-[#302B2B]">
                          {item.display_name.split(",")[0]}
                        </div>
                        <div className="text-[13px] text-[#9A99AB] truncate">
                          {item.display_name}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </div>

              {position && (
                <div className="w-full h-[212px] rounded-xl overflow-hidden border shadow-sm">
                  <MapContainer
                    center={position}
                    zoom={14}
                    style={{ height: 260, width: "100%" }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution="&copy; OpenStreetMap contributors"
                    />
                    <LocationPicker
                      position={position}
                      setPosition={setPosition}
                      setForm={setForm}
                    />
                  </MapContainer>
                </div>
              )}
            </>
          )}

          {userType === "salon" && (
            <>
              <div className="flex gap-6">
                <InputField
                  label="Company Name"
                  value={form.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                />
                <CustomDropdown
                  label="Currency"
                  icon={<DollarSign className="w-5 h-5" />}
                  options={currencies.map((c) => ({
                    value: c.short_name,
                    label: c.short_name,
                    icon: c.icon,
                  }))}
                  value={form.currency}
                  onChange={(val) => handleChange("currency", val)}
                  placeholder="Select currency"
                />
              </div>

              <div className="flex flex-col gap-2 relative">
                <label className="text-[#302B2B] text-[14px] font-normal">
                  Address
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3.5">
                    <MapPin className="w-5 h-5 text-gray-400" />
                  </span>
                  <input
                    type="text"
                    value={form.address}
                    placeholder="Enter address"
                    onFocus={() => setAddressFocused(true)}
                    onBlur={() =>
                      setTimeout(() => setAddressFocused(false), 200)
                    }
                    onChange={(e) => handleChange("address", e.target.value)}
                    className="pl-10 border border-[#9A99AB] rounded-xl px-3 py-3 text-[16px] text-[#302B2B] placeholder-[#9A99AB] w-full focus:outline-none focus:ring-2 focus:ring-[#5563F6]"
                  />
                </div>

                {suggestions.length > 0 && (
                  <motion.div className="absolute top-full mt-2 left-0 w-full bg-white border border-[#9A99AB] rounded-xl shadow-lg z-[9999] max-h-[220px] overflow-y-auto no-scrollbar">
                    {suggestions.map((item) => (
                      <div
                        key={item.place_id}
                        onClick={() => handleAddressSelect(item)}
                        className="px-4 py-2 cursor-pointer hover:bg-[#F5F6FF] transition"
                      >
                        <div className="text-[14px] font-medium text-[#302B2B]">
                          {item.display_name.split(",")[0]}
                        </div>
                        <div className="text-[13px] text-[#9A99AB] truncate">
                          {item.display_name}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </div>

              {position && (
                <div className="w-full h-[212px] rounded-xl overflow-hidden border shadow-sm">
                  <MapContainer
                    center={position}
                    zoom={14}
                    style={{ height: 260, width: "100%" }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution="&copy; OpenStreetMap contributors"
                    />
                    <LocationPicker
                      position={position}
                      setPosition={setPosition}
                      setForm={setForm}
                    />
                  </MapContainer>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-4 mt-4">
          <button
            onClick={onClose}
            className="text-gray-500 text-[16px] hover:underline"
          >
            Cancel
          </button>
          <button
            disabled={loading}
            onClick={handleSubmit}
            className="bg-[#5563F6] text-white rounded-xl px-6 py-3 font-medium hover:bg-[#4453E3] transition flex items-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
